/* ---------------------------------------------------------------------------
 * The programmatic half of the motion system.
 *
 * `index.css` owns every duration and every curve. `ui/motion/motion.css` owns
 * the declarative enter variants. This file exists only for the two jobs CSS
 * cannot do on its own:
 *
 *   - animating a change whose start value is not known until it has already
 *     happened — a row moving because the row above it was deleted (FLIP)
 *   - coordinating a DOM swap across a paint (view transitions)
 *
 * It uses the platform's own APIs and nothing else: Web Animations, view
 * transitions, `matchMedia`, `CSS.supports`. No dependency was added for this.
 *
 * ---------------------------------------------------------------------------
 * FOUR RULES THIS FILE IS BUILT ON
 * ---------------------------------------------------------------------------
 *
 * 1. NO NUMBER FROM `index.css` IS COPIED HERE. Not one. Canonical base
 *    durations, the global scale and curves are read back off the document root
 *    at call time with `getComputedStyle`, so CSS and WAAPI move together. The
 *    consequence is deliberate and is the reason it works: **if a token cannot
 *    be read, the helper animates nothing and returns `null`** rather than
 *    falling back to an invented number. A silent 300ms that no token controls
 *    is how two motion systems drift apart.
 *
 * 2. REDUCED MOTION REMOVES MOVEMENT, NOT FEEDBACK. Under
 *    `prefers-reduced-motion: reduce` every spatial property — transform,
 *    translate, rotate, scale, filter, clip-path — is stripped out of the
 *    keyframes and what survives (in practice, opacity) plays at
 *    `--busee-motion-fast`. A change still reads as a change; nothing travels.
 *    If stripping leaves nothing that would visibly change, the animation is
 *    skipped entirely rather than run as an empty one. This is a *narrowing* of
 *    the global rule in `index.css`, not a second copy of it — see the note on
 *    `withoutMovement`.
 *
 * 3. EVERY CAPABILITY IS FEATURE-DETECTED, because the same bundle is served
 *    three ways (Tauri's WKWebView, a mobile PWA, a desktop browser) and the
 *    oldest of those is the one that matters. `Element.animate`,
 *    `document.startViewTransition`, `Element.getAnimations` and `linear()`
 *    easing are each checked before use, and the absence of any one of them
 *    degrades to "the change happens, it just does not animate".
 *
 * 4. THE ENVIRONMENT IS A PARAMETER. Every public helper takes an optional
 *    {@link MotionEnv} as its last argument, defaulting to the live one. That
 *    is what makes this file testable in the repo's `environment: "node"` test
 *    setup without a DOM and without adding jsdom — and it costs one line per
 *    helper.
 * ------------------------------------------------------------------------- */

/**
 * The duration tokens, i.e. the `--busee-motion-*` column of IOS-DESIGN §6.
 *
 * A union rather than a `string`, so a typo is a compile error instead of a
 * missing token that silently disables an animation at runtime.
 */
export type MotionDuration = "fast" | "exit" | "enter" | "snappy" | "sheet" | "spring" | "bounce";

/** The easing tokens: the `--ease-*` half of the same table. */
export type MotionEasing =
  | "standard"
  | "enter"
  | "exit"
  | "spring"
  | "spring-snappy"
  | "spring-bounce";

/**
 * Everything about the machine and the user that motion depends on.
 *
 * Passed explicitly so the helpers are pure functions of it. {@link liveEnv}
 * builds the real one; a test builds a literal.
 */
export type MotionEnv = {
  /** `prefers-reduced-motion: reduce` is in force. */
  reduced: boolean;
  /** The engine understands `linear()` easing (Safari 17.2+, Chrome 113+). */
  linear: boolean;
  /** Reads a custom property off the document root. `""` when it is not set. */
  readToken: (name: string) => string;
};

/* --- Reading the token layer ---------------------------------------------- */

let tokenCache: Map<string, string> | null = null;
let linearSupport: boolean | null = null;

/**
 * A `--busee-*` / `--ease-*` value off `:root`, memoised.
 *
 * `getComputedStyle` is a synchronous style resolution and these are read on
 * interaction paths (every list reflow), so the handful of motion tokens are
 * cached after first read. They are safe to cache in a way colours are not:
 * the theme flips with `prefers-color-scheme` and moves every colour token, but
 * no motion token has a second value in either theme or at any density.
 */
function readTokenLive(name: string): string {
  if (typeof document === "undefined") {
    return "";
  }
  const cache = tokenCache ?? new Map<string, string>();
  tokenCache = cache;
  const hit = cache.get(name);
  if (hit !== undefined) {
    return hit;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  cache.set(name, value);
  return value;
}

/**
 * Forget the memoised token values.
 *
 * Nothing in the app needs this today — motion tokens do not change with the
 * theme or the density. It exists for the case that would otherwise be a silent
 * wrong answer: a future runtime override of a motion token on `:root`.
 */
export function clearMotionTokenCache(): void {
  tokenCache = null;
}

/**
 * Whether the user asked for reduced motion, read live.
 *
 * Not cached: the setting is changed in System Settings while the app is open —
 * Kobo is a menu-bar app people never quit — and `matchMedia` already returns
 * a memoised `MediaQueryList`, so reading `.matches` per call is free.
 *
 * With no `window` (the test environment) this answers `true`: outside a
 * browser nothing can be animated, and "reduced" is the honest spelling of
 * that.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Whether `linear()` easing is understood.
 *
 * This is the one that bites in the Tauri shell. The three spring curves in
 * `index.css` are `linear()` ramps sampled from a damped oscillator, and WAAPI
 * parses its `easing` option as a CSS `<easing-function>` — so on an engine
 * without `linear()` support, passing one **throws a `TypeError`** and takes
 * the caller's click handler down with it. CSS is forgiving here (the
 * declaration is dropped, the animation runs linear); WAAPI is not.
 *
 * Cached, because engine support cannot change inside a session.
 */
export function supportsLinearEasing(): boolean {
  if (linearSupport !== null) {
    return linearSupport;
  }
  const supported =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("transition-timing-function", "linear(0, 1)");
  linearSupport = supported;
  return supported;
}

/** The real environment: the user's setting, the engine's support, `:root`. */
export function liveEnv(): MotionEnv {
  return {
    reduced: prefersReducedMotion(),
    linear: supportsLinearEasing(),
    readToken: readTokenLive,
  };
}

/**
 * A CSS `<time>` in milliseconds, or `null` if it is not one.
 *
 * Custom properties compute to their token stream, not to a canonical unit, so
 * this sees exactly what `index.css` wrote — `240ms` today, `0.24s` if someone
 * writes it that way tomorrow. Both are legal CSS and both must parse.
 */
export function parseMs(value: string): number | null {
  const match = /^(\d*\.?\d+)(ms|s)$/i.exec(value.trim());
  if (!match) {
    return null;
  }
  const [, amount, unit] = match;
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return unit.toLowerCase() === "s" ? parsed * 1000 : parsed;
}

/**
 * Count the user-perceived characters in a string.
 *
 * Typewriter motion needs a CSS `steps()` count, and UTF-16 length is wrong
 * for Thai vowel marks, emoji joined with a ZWJ, and composed Latin text. The
 * platform segmenter is the source of truth where it exists. Older webviews
 * still get a safe code-point fallback: it may use extra visual steps for a
 * joined emoji, but it never splits the string into DOM nodes or changes the
 * text that assistive technology reads.
 */
export function countGraphemes(value: string): number {
  type SegmenterConstructor = new (
    locales?: string | string[],
    options?: { granularity?: "grapheme" },
  ) => { segment: (input: string) => Iterable<unknown> };

  const Segmenter =
    typeof Intl === "undefined"
      ? undefined
      : (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter;

  if (Segmenter !== undefined) {
    return [...new Segmenter(undefined, { granularity: "grapheme" }).segment(value)].length;
  }

  return Array.from(value).length;
}

/** A positive unitless motion scale, or `null` if it is not one. */
function parseMotionScale(value: string): number | null {
  const trimmed = value.trim();
  if (!/^(?:\d+|\d*\.\d+)$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function motionScale(env: MotionEnv): number | null {
  return parseMotionScale(env.readToken("--busee-motion-scale"));
}

function baseDurationMs(name: MotionDuration, env: MotionEnv): number | null {
  return parseMs(env.readToken(`--busee-motion-${name}-base`));
}

/** A scaled duration in milliseconds, or `null` if either source token is unreadable. */
export function durationMs(name: MotionDuration, env: MotionEnv = liveEnv()): number | null {
  const base = baseDurationMs(name, env);
  const scale = motionScale(env);
  return base === null || scale === null ? null : base * scale;
}

/**
 * An easing token as a WAAPI `easing` string, or `null` if it is not set.
 *
 * Where the token is a `linear()` ramp and the engine cannot parse one, this
 * substitutes `--ease-standard` — read from the token layer, not written out
 * here. `standard` is the right substitute because IOS-DESIGN §6 already names
 * it "the general-purpose non-spring option": the overshoot is lost, which is
 * unavoidable, but the motion keeps the iOS sheet curve rather than falling to
 * a linear ramp that reads as broken.
 */
export function easingValue(name: MotionEasing, env: MotionEnv = liveEnv()): string | null {
  const value = env.readToken(`--ease-${name}`).trim();
  if (value === "") {
    return null;
  }
  if (env.linear || !value.startsWith("linear(")) {
    return value;
  }
  const fallback = env.readToken("--ease-standard").trim();
  return fallback === "" ? null : fallback;
}

/* --- Reduced motion ------------------------------------------------------- */

/**
 * The properties that move something through space.
 *
 * `filter` and `clip-path` are on the list with the transforms because a blur
 * or a wipe reads as motion to the same vestibular system the setting exists to
 * protect, not because they change layout.
 */
const MOVEMENT_PROPERTIES = new Set([
  "transform",
  "translate",
  "rotate",
  "scale",
  "filter",
  "backdrop-filter",
  "backdropFilter",
  "clip-path",
  "clipPath",
]);

/** Keyframe keys that are timing metadata rather than an animated property. */
const KEYFRAME_METADATA = new Set(["offset", "easing", "composite"]);

/**
 * The same keyframes with every spatial property removed.
 *
 * IOS-DESIGN §6 used to say "do not implement a second copy of the
 * reduced-motion check per component", and that rule still holds — but it was
 * written when the only mechanism was CSS, where the global rule in
 * `index.css` clamps every animation to 0.01ms and there is nothing left to
 * decide. WAAPI is not reached by that rule at all: an `Element.animate()` call
 * carries its own duration and ignores the stylesheet completely. So the choice
 * here is not "check reduced motion twice", it is "honour it in the one place
 * the global rule cannot see" — and honouring it by dropping to 0.01ms would
 * make a deletion appear to teleport, which is worse than the fade this leaves
 * behind.
 */
export function withoutMovement(keyframes: readonly Keyframe[]): Keyframe[] {
  return keyframes.map((frame) => {
    const kept: Keyframe = {};
    for (const property of Object.keys(frame)) {
      if (!MOVEMENT_PROPERTIES.has(property)) {
        kept[property] = frame[property];
      }
    }
    return kept;
  });
}

/** Whether a keyframe list still animates anything after the strip above. */
function animatesSomething(keyframes: readonly Keyframe[]): boolean {
  return keyframes.some((frame) =>
    Object.keys(frame).some((property) => !KEYFRAME_METADATA.has(property)),
  );
}

/**
 * Opt into compositor preparation only for the animation that is about to run.
 *
 * Keeping `will-change` on every list row costs memory for the entire lifetime
 * of a screen.  Preserve an author-supplied value, otherwise restore the empty
 * inline value as soon as WAAPI settles (including cancellation).
 */
function setTemporaryWillChange(target: Element, keyframes: readonly Keyframe[]): (() => void) | undefined {
  const style = (target as Element & { style?: CSSStyleDeclaration }).style;
  if (!style || style.willChange !== "") {
    return undefined;
  }

  const properties = new Set<string>();
  for (const frame of keyframes) {
    for (const property of Object.keys(frame)) {
      if (!KEYFRAME_METADATA.has(property) && frame[property] !== undefined) {
        properties.add(property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`));
      }
    }
  }
  if (properties.size === 0) {
    return undefined;
  }

  style.willChange = [...properties].join(", ");
  return () => {
    style.willChange = "";
  };
}

/* --- Web Animations ------------------------------------------------------- */

/** How an animation is timed. Both halves name a token; neither names a number. */
export type MotionOptions = {
  duration: MotionDuration;
  easing: MotionEasing;
  /**
   * Milliseconds before the animation starts. Dropped under reduced motion —
   * a stagger is movement in time, which is the thing being reduced.
   */
  delay?: number;
  fill?: FillMode;
  /** Tags the animation so {@link cancelFlip} and friends can find it again. */
  id?: string;
};

/**
 * Run a Web Animation with its timing taken from the token layer.
 *
 * Returns `null` — having animated nothing — when the engine has no WAAPI, when
 * a required token is unreadable, or when reduced motion has stripped the
 * keyframes down to nothing. Callers must treat `null` as "the change is
 * already visible", which it is: none of this changes the DOM, it only animates
 * a change that has already been made.
 */
export function animate(
  target: Element,
  keyframes: readonly Keyframe[],
  options: MotionOptions,
  env: MotionEnv = liveEnv(),
): Animation | null {
  if (typeof target.animate !== "function") {
    return null;
  }

  const frames = env.reduced ? withoutMovement(keyframes) : [...keyframes];
  if (!animatesSomething(frames)) {
    return null;
  }

  const scale = motionScale(env);
  // Reduced motion deliberately keeps its short fade unscaled, but still
  // requires a readable global token rather than inventing a timing.
  const duration = env.reduced ? baseDurationMs("fast", env) : durationMs(options.duration, env);
  const easing = easingValue(env.reduced ? "standard" : options.easing, env);
  if (duration === null || easing === null || scale === null) {
    return null;
  }

  let animation: Animation;
  const restoreWillChange = setTemporaryWillChange(target, frames);
  try {
    animation = target.animate(frames, {
      duration,
      easing,
      delay: env.reduced ? 0 : options.delay === undefined ? undefined : options.delay * scale,
      fill: options.fill,
    });
  } catch {
    restoreWillChange?.();
    return null;
  }
  if (options.id !== undefined) {
    animation.id = options.id;
  }
  if (restoreWillChange) {
    void animation.finished.then(restoreWillChange, restoreWillChange);
  }
  return animation;
}

/* --- View transitions ----------------------------------------------------- */

/** The attribute `ui/motion`'s enter variants are selected by. */
export const ENTER_ATTRIBUTE = "data-busee-enter";

/**
 * The seal that permanently disables an element's CSS entrance.
 *
 * Written **imperatively, once**, by {@link sealEnterAnimations} — never
 * returned from a render. That distinction is the whole bug it fixes. It used
 * to be a prop computed from a module-level "a transition is running" flag, so
 * the first re-render after the transition (a query resolving, a subscription
 * firing) handed React `undefined` for an attribute it had already written,
 * React removed it, and every sealed element replayed its entrance a beat
 * *after* the browser's crossfade had finished. React never removes an
 * attribute it did not render, which is what makes an imperative write stick.
 */
export const ENTER_SKIP_ATTRIBUTE = "data-busee-enter-skip";

/**
 * Seal every CSS entrance currently in the document.
 *
 * Called from inside the transition's update callback, at the instant the
 * browser is about to snapshot the new DOM: whatever carries an enter variant
 * at that moment is part of the arrival the browser is already animating, and
 * must not also animate itself underneath the crossfade. Anything that mounts
 * *later* — the rows that arrive when a query resolves — is untouched and
 * enters normally, which is the behaviour that makes a late-loading screen
 * still feel alive.
 */
function sealEnterAnimations(): void {
  const pending = document.querySelectorAll(
    `[${ENTER_ATTRIBUTE}]:not([${ENTER_SKIP_ATTRIBUTE}])`,
  );
  for (const element of pending) {
    element.setAttribute(ENTER_SKIP_ATTRIBUTE, "");
  }
}

/**
 * Apply a DOM change inside a view transition where the engine has one.
 *
 * Progressive by construction: without `document.startViewTransition` the
 * update simply runs, and the caller cannot tell the difference except that
 * nothing crossfades. Safari gained same-document view transitions in 18, so
 * the Tauri shell has this on a current macOS and does not on an older one —
 * which is exactly the shape "progressive" has to mean here.
 *
 * Three things a caller must know:
 *
 * - **React updates must be flushed synchronously.** The browser captures the
 *   "new" state the moment the callback returns, so a plain `setState` — which
 *   React batches — captures the *old* DOM and crossfades a frame into itself.
 *   A caller uses `flushSync`.
 * - **`flushSync` cannot flush a `startTransition`**, and that is the trap this
 *   API is easiest to get wrong with, because it fails silently: the callback
 *   returns, the transition runs perfectly, and it animates the wrong DOM.
 *   Keep this helper for synchronous, local surface swaps such as Workout's
 *   routine manager; persistent top-level navigation must not use it because
 *   the root snapshot overlay temporarily blocks repeat clicks on app chrome.
 * - **Reduced motion is handled in CSS, not here**, because the thing being
 *   timed is a UA pseudo-element (`::view-transition-old/new(root)`) that no
 *   JavaScript owns. `ui/motion/motion.css` tokenises its duration and shortens
 *   it under `prefers-reduced-motion`. That crossfade carries no movement, so
 *   there is nothing to suppress — only something to shorten.
 *
 * The returned promise settles when the DOM has been updated, not when the
 * animation has finished, and rejects only if `update` itself throws.
 */
export function startViewTransition(update: () => void): Promise<void> {
  if (typeof document === "undefined" || typeof document.startViewTransition !== "function") {
    try {
      update();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  let transition: ViewTransition;
  try {
    transition = document.startViewTransition(() => {
      update();
      // Same synchronous turn as the commit, so the seal is in the DOM the
      // browser is about to photograph rather than one frame behind it.
      sealEnterAnimations();
    });
  } catch (error) {
    return Promise.reject(error);
  }
  // `finished` rejects when a transition is skipped — which happens routinely,
  // e.g. two navigations in the same frame — and an unobserved rejection there
  // would surface as an unhandled promise rejection in the console of a build
  // that is otherwise working perfectly.
  transition.finished.catch(() => undefined);
  return transition.updateCallbackDone;
}

/* --- FLIP ----------------------------------------------------------------- */

/**
 * The attribute an element wears to take part in a list transition, and the
 * value that identifies it across a reflow — a task id, an issue key, anything
 * stable. `ui/motion`'s `flipProps` is the way to write it.
 */
export const FLIP_KEY_ATTRIBUTE = "data-busee-flip";

/** Tags every animation this file starts for a FLIP, so it can be cancelled. */
export const FLIP_ANIMATION_ID = "busee-flip";

const FLIP_SELECTOR = `[${FLIP_KEY_ATTRIBUTE}]`;

/**
 * Sub-pixel movement is not movement. Rounding differences between two layout
 * passes routinely produce fractions of a pixel, and animating those is a
 * compositor layer per row for a change nobody can see.
 */
const FLIP_EPSILON = 1;

/**
 * How many rows may move before the whole reflow is left alone.
 *
 * Not a design token — a count, not a duration — and deliberately low. When
 * more than this many rows move at once the cause is almost never a list edit:
 * it is a filter change, a route change or a resize, where every row moves and
 * animating all of them is both expensive and wrong (the user did not move one
 * thing, they replaced the screen). The kanban board carries 441 cards; this is
 * the guard that keeps a reflow there from starting hundreds of animations.
 */
const FLIP_MAX_ELEMENTS = 24;

/** Where a keyed element sat, in the list's own coordinate space. */
export type FlipPoint = { readonly x: number; readonly y: number };

/** Where every keyed element sat at the end of the previous commit. */
export type FlipSnapshot = ReadonlyMap<string, FlipPoint>;

export type FlipOptions = {
  /** Defaults to `snappy` — optimistic and keyboard reflows must settle quickly. */
  duration?: MotionDuration;
  easing?: MotionEasing;
  /** Override {@link FLIP_MAX_ELEMENTS} for a list known to be short. */
  maxElements?: number;
};

/**
 * Measure every keyed descendant, relative to the list's own content origin.
 *
 * Relative and scroll-corrected rather than viewport-absolute on purpose: a
 * viewport measurement makes every row in the list "move" the moment the page
 * or the list scrolls between two commits, which would fire a full-list
 * animation on a scroll. Subtracting the container's own rect and adding its
 * scroll offset removes both.
 */
export function captureFlipPositions(root: Element): FlipSnapshot {
  const positions = new Map<string, FlipPoint>();
  const rootRect = root.getBoundingClientRect();
  const originX = rootRect.left - root.scrollLeft;
  const originY = rootRect.top - root.scrollTop;

  for (const element of root.querySelectorAll(FLIP_SELECTOR)) {
    const key = element.getAttribute(FLIP_KEY_ATTRIBUTE);
    // A duplicate key is a caller bug; taking the first keeps it deterministic.
    if (key === null || key === "" || positions.has(key)) {
      continue;
    }
    const rect = element.getBoundingClientRect();
    positions.set(key, { x: rect.left - originX, y: rect.top - originY });
  }
  return positions;
}

/**
 * Stop any FLIP still running inside `root`.
 *
 * Called before measuring, and that ordering is the whole point:
 * `getBoundingClientRect` reports the *transformed* box, so measuring a row
 * that is still mid-FLIP reads its old position and the next animation
 * compounds the error until the list drifts.
 */
export function cancelFlip(root: Element): void {
  for (const element of root.querySelectorAll(FLIP_SELECTOR)) {
    if (typeof element.getAnimations !== "function") {
      continue;
    }
    for (const animation of element.getAnimations()) {
      if (animation.id === FLIP_ANIMATION_ID) {
        animation.cancel();
      }
    }
  }
}

/**
 * Animate the gap between where things were and where they now are.
 *
 * The DOM change has already happened and is already painted-correct; this only
 * moves each row back to where it was and lets it travel to where it is. It
 * therefore cannot delay, block or undo an optimistic write — the rule in
 * `HANDOFF.md` that no animation may hold up the frame in which a card changes
 * lane survives because the frame has already been committed.
 *
 * Returns the *true* current layout so the caller can hold it as the next
 * `previous`. Reading it from the return value rather than measuring again is
 * not a convenience: by the time this returns, the rows are transformed away
 * from their real positions and a second measurement would capture the lie.
 */
export function playFlip(
  root: Element,
  previous: FlipSnapshot | null,
  options: FlipOptions = {},
  env: MotionEnv = liveEnv(),
): FlipSnapshot {
  cancelFlip(root);
  const current = captureFlipPositions(root);

  // Under reduced motion the rows are already where they belong: a FLIP is
  // movement and nothing else, so there is no fade to preserve here.
  if (previous === null || env.reduced) {
    return current;
  }

  const moves: { element: Element; dx: number; dy: number }[] = [];
  for (const element of root.querySelectorAll(FLIP_SELECTOR)) {
    const key = element.getAttribute(FLIP_KEY_ATTRIBUTE);
    if (key === null) {
      continue;
    }
    const from = previous.get(key);
    const to = current.get(key);
    // No `from` means the row was not in the previous arrangement: it is new,
    // it fades in with `enterProps`, and it has no previous position to travel
    // from. "New" and "moved" are different channels and must stay that way.
    if (from === undefined || to === undefined) {
      continue;
    }
    const dx = from.x - to.x;
    const dy = from.y - to.y;
    if (Math.abs(dx) < FLIP_EPSILON && Math.abs(dy) < FLIP_EPSILON) {
      continue;
    }
    moves.push({ element, dx, dy });
  }

  if (moves.length === 0 || moves.length > (options.maxElements ?? FLIP_MAX_ELEMENTS)) {
    return current;
  }

  for (const { element, dx, dy } of moves) {
    animate(
      element,
      // `transform` only, and `none` rather than `translate(0, 0)` at the end:
      // the row must be left with no transform of its own, so a child that
      // needs `position: fixed` or a popover anchored to it is not living
      // inside a containing block this file created.
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
      {
        duration: options.duration ?? "snappy",
        easing: options.easing ?? "spring-snappy",
        id: FLIP_ANIMATION_ID,
      },
      env,
    );
  }

  return current;
}
